import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import dayjs from 'dayjs';
import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate, useParams } from 'react-router';

import { useGetVersionDrilldownInfiniteQuery } from 'src/apiClients';
import type { VersionDrilldownItem } from 'src/apiClients/queries';
import { NO_VERSION } from 'src/apiClients/queries/active-versions-query';
import { DataTable } from 'src/components/data-table/DataTable';
import {
    SortDirection,
    type DataTableCellValue,
    type DataTableColDef,
    type DataTableRow,
    type DataTableSort,
} from 'src/components/data-table/DataTable.types';
import { useEntityListQueryState } from 'src/components/entity-list-page/hooks/use-entity-list-query-state';
import { ROUTES } from 'src/utils/strings';

import { i18n } from './ActiveVersionsDrilldownPage.i18n';
import { useStyles } from './ActiveVersionsDrilldownPage.styles';

const PRODUCT_DISPLAY_NAMES: Record<string, string> = {
    imagoadmin: 'Admin',
    imagoapi: 'API',
    imagocapture: 'Capture X',
    imagocmd: 'CMD',
    imagoconnect: 'Connect',
    imagomobile: 'Mobile',
    imagoportal: 'Portal',
    leapfrog: 'Leapfrog',
};

const SEARCH_COLUMNS = ['subscriptionName', 'login'];
const INITIAL_SORT: DataTableSort[] = [{ field: 'latestAttempt', direction: SortDirection.DESC }];

const formatDateTime = (value: DataTableCellValue): string => {
    if (!value || typeof value !== 'string') return '';
    const parsed = dayjs(value);
    if (!parsed.isValid()) return String(value);
    return parsed.format('D MMM YYYY, hh:mm A');
};

const mapRow = (item: VersionDrilldownItem): DataTableRow => ({
    id: item.id,
    totalCount: item.totalCount,
    subscriptionName: item.subscriptionName,
    login: item.login,
    latestAttempt: item.latestAttempt,
    version: item.version,
});

export const ActiveVersionsDrilldownPage = () => {
    const intl = useIntl();
    const navigate = useNavigate();
    const { classes } = useStyles();
    const { product, version } = useParams<{ product: string; version: string }>();

    const productDisplayName = product ? (PRODUCT_DISPLAY_NAMES[product] ?? product) : '';
    const hasVersion = version && version !== NO_VERSION;
    const pageTitle = hasVersion ? `${productDisplayName} ${version}` : productDisplayName;

    const baseWhere = useMemo(
        () => ({
            ...(product ? { product } : {}),
            ...(hasVersion ? { version } : {}),
        }),
        [product, version, hasVersion],
    );

    const columns = useMemo<DataTableColDef[]>(
        () => [
            {
                field: 'subscriptionName',
                headerName: intl.formatMessage(i18n.subscriptionName),
                flex: 1,
                minWidth: 200,
            },
            {
                field: 'login',
                headerName: intl.formatMessage(i18n.userLogin),
                flex: 1,
                minWidth: 200,
            },
            {
                field: 'totalCount',
                headerName: intl.formatMessage(i18n.totalSignInsColumn),
                width: 140,
                copyable: false,
            },
            {
                field: 'latestAttempt',
                headerName: intl.formatMessage(i18n.latestAttempt),
                width: 220,
                valueFormatter: formatDateTime,
            },
        ],
        [intl],
    );

    const { queryState, setQueryState, queryArg } = useEntityListQueryState({
        searchColumns: SEARCH_COLUMNS,
        initialSort: INITIAL_SORT,
        baseWhere,
    });

    const { data, isLoading, isFetching, isError } = useGetVersionDrilldownInfiniteQuery(queryArg);

    const rows = useMemo<DataTableRow[]>(() => {
        if (!data) return [];
        const allRows = data.pages.flatMap((p) => p.rows.map(mapRow));
        // The server cannot filter `version IS NULL` via query string (qs parses
        // all values as strings), so when viewing the no-version bucket we fetch
        // all versions for the product and filter client-side.
        if (!hasVersion) {
            return allRows.filter((r) => !r.version);
        }
        return allRows;
    }, [data, hasVersion]);

    // Derive total from filtered rows — limit=1000 means a single page so
    // .length is exact and stays consistent with the no-version filter above.
    const total = rows.length;
    const uniqueUsers = useMemo(() => {
        const logins = new Set(rows.map((r) => r.login));
        return logins.size;
    }, [rows]);
    const totalSignIns = useMemo(() => {
        return rows.reduce((sum, r) => sum + (Number(r.totalCount) || 0), 0);
    }, [rows]);

    const handleRowClick = useCallback(
        (row: DataTableRow) => {
            if (row.id && typeof row.id === 'string') {
                navigate(`${ROUTES.INSTANCES}/${row.id}`);
            }
        },
        [navigate],
    );

    return (
        <Box className={classes.root} automation-id="active-versions-drilldown-page">
            {/* Header */}
            <Box className={classes.header}>
                <Typography variant="h5" className={classes.headerTitle}>
                    {pageTitle}
                </Typography>
                {hasVersion && (
                    <Chip size="small" label={version} color="primary" variant="outlined" />
                )}
            </Box>

            {/* Summary card */}
            <Box className={classes.summaryCard}>
                <Box className={classes.summaryField}>
                    <Typography className={classes.summaryLabel}>
                        {intl.formatMessage(i18n.productLabel)}
                    </Typography>
                    <Typography className={classes.summaryValue}>{productDisplayName}</Typography>
                </Box>
                <Box className={classes.summaryField}>
                    <Typography className={classes.summaryLabel}>
                        {intl.formatMessage(i18n.versionLabel)}
                    </Typography>
                    <Typography className={classes.summaryValue}>
                        {hasVersion ? version : '(No version)'}
                    </Typography>
                </Box>
                <Box className={classes.summaryField}>
                    <Typography className={classes.summaryLabel}>
                        {intl.formatMessage(i18n.totalSignInsLabel)}
                    </Typography>
                    <Typography className={classes.summaryValue}>
                        {isLoading ? '—' : totalSignIns.toLocaleString()}
                    </Typography>
                </Box>
                <Box className={classes.summaryField}>
                    <Typography className={classes.summaryLabel}>
                        {intl.formatMessage(i18n.uniqueUsersLabel)}
                    </Typography>
                    <Typography className={classes.summaryValue}>
                        {isLoading ? '—' : uniqueUsers.toLocaleString()}
                    </Typography>
                </Box>
            </Box>

            {/* Sign-ins table section */}
            <Box className={classes.tableSection}>
                <Box className={classes.sectionHeading}>
                    <Typography variant="h5" className={classes.sectionTitle}>
                        {intl.formatMessage(i18n.signInsHeading)}
                    </Typography>
                    {total > 0 && (
                        <Chip
                            size="small"
                            label={total}
                            color="default"
                            className={classes.countChip}
                        />
                    )}
                </Box>
                <DataTable
                    columns={columns}
                    rows={isError ? [] : rows}
                    isLoading={isLoading && !isError}
                    onRowClick={handleRowClick}
                    queryState={queryState}
                    onQueryStateChange={setQueryState}
                    totalRows={total}
                    hasMore={false}
                    isLoadingMore={isFetching && !isLoading}
                    onLoadMore={() => {}}
                    titleWhenNoResults={intl.formatMessage(i18n.noResultsTitle)}
                    messageWhenNoResults={intl.formatMessage(i18n.noResultsMessage)}
                />
            </Box>
        </Box>
    );
};
